import { useEffect, useRef, useState } from 'react';

import { Consumer, Subscription } from '@rails/actioncable';

import { useActionCable } from 'components/ActionCableProvider';

type UseWebsocketSubscriptionOptions<T> = {
  channel: string;
  params?: Record<string, unknown>;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onReceived?: (data: T) => void;
  onRejected?: () => void;
  enabled?: boolean;
};

type UseWebsocketSubscriptionReturn = {
  subscription: Subscription | null;
  isConnected: boolean;
};

// ---------------------------------------------------------------------------
// Shared subscription registry
//
// Multiple React components may subscribe to the same ActionCable channel with
// identical params (e.g. the list-level AdmissionJobSubscriber and the
// AIJobNotificationManager's JobSubscriber for the same AI job).
//
// ActionCable's SubscriptionGuarantor retries every 500 ms until
// confirm_subscription arrives. With two Subscription objects sharing one
// identifier the guarantor's object-identity-based `forget` can miss the
// second object, causing an infinite retry loop.
//
// This registry ref-counts subscriptions by identifier so only ONE
// ActionCable Subscription exists per unique identifier. Additional callers
// share it, each receiving callbacks independently.
// ---------------------------------------------------------------------------

type CallbackSet = {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onReceived?: (data: unknown) => void;
  onRejected?: () => void;
};

type RegistryEntry = {
  subscription: Subscription;
  refCount: number;
  connected: boolean;
  listeners: Set<CallbackSet>;
};

const registry = new Map<string, RegistryEntry>();

function buildIdentifier(channel: string, params: Record<string, unknown>): string {
  return JSON.stringify({ channel, ...params });
}

function acquireSubscription(
  consumer: Consumer,
  channel: string,
  params: Record<string, unknown>,
  callbacks: CallbackSet
): RegistryEntry {
  const identifier = buildIdentifier(channel, params);
  const existing = registry.get(identifier);

  if (existing) {
    existing.refCount++;
    existing.listeners.add(callbacks);

    // If already connected, fire connected callback immediately so the new
    // subscriber's isConnected state is accurate from the start.
    if (existing.connected) callbacks.onConnected?.();

    return existing;
  }

  const listeners = new Set<CallbackSet>([callbacks]);
  const entry: RegistryEntry = {
    subscription: null as unknown as Subscription,
    refCount: 1,
    connected: false,
    listeners,
  };

  const sub = consumer.subscriptions.create(
    { channel, ...params },
    {
      connected: () => {
        entry.connected = true;
        for (const cb of entry.listeners) cb.onConnected?.();
      },
      disconnected: () => {
        entry.connected = false;
        for (const cb of entry.listeners) cb.onDisconnected?.();
      },
      received: (data: unknown) => {
        for (const cb of entry.listeners) cb.onReceived?.(data);
      },
      rejected: () => {
        for (const cb of entry.listeners) cb.onRejected?.();
      },
    }
  );

  entry.subscription = sub;
  registry.set(identifier, entry);
  return entry;
}

function releaseSubscription(channel: string, params: Record<string, unknown>, callbacks: CallbackSet): void {
  const identifier = buildIdentifier(channel, params);
  const entry = registry.get(identifier);
  if (!entry) return;

  entry.listeners.delete(callbacks);
  entry.refCount--;

  if (entry.refCount <= 0) {
    entry.subscription.unsubscribe();
    registry.delete(identifier);
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useWebsocketSubscription = <T,>({
  channel,
  params = {},
  onConnected,
  onDisconnected,
  onReceived,
  onRejected,
  enabled = true,
}: UseWebsocketSubscriptionOptions<T>): UseWebsocketSubscriptionReturn => {
  const { consumer } = useActionCable();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Store callbacks in a ref so the registry always calls the latest version
  // without needing to re-subscribe when handlers change.
  const callbacksRef = useRef<CallbackSet>({});
  callbacksRef.current = {
    onConnected: () => {
      setIsConnected(true);
      onConnected?.();
    },
    onDisconnected: () => {
      setIsConnected(false);
      onDisconnected?.();
    },
    onReceived: (data: unknown) => {
      onReceived?.(data as T);
    },
    onRejected: () => {
      onRejected?.();
    },
  };

  // Stable wrapper object whose methods delegate to callbacksRef.current.
  // This object is added/removed from the registry's listener set by identity.
  const stableCallbacksRef = useRef<CallbackSet | null>(null);
  if (!stableCallbacksRef.current) {
    stableCallbacksRef.current = {
      onConnected: () => callbacksRef.current.onConnected?.(),
      onDisconnected: () => callbacksRef.current.onDisconnected?.(),
      onReceived: (data: unknown) => callbacksRef.current.onReceived?.(data),
      onRejected: () => callbacksRef.current.onRejected?.(),
    };
  }

  // Keyed by the serialized identifier, not the params object: params is a fresh
  // object every render (identity would thrash), but its CONTENT changing — e.g.
  // navigating from one workflow id to another without an unmount — must tear down
  // the old subscription and establish the new one.
  const identifier = buildIdentifier(channel, params);

  useEffect(() => {
    if (!consumer || !enabled) {
      return;
    }

    const callbacks = stableCallbacksRef.current!;
    const entry = acquireSubscription(consumer, channel, params, callbacks);

    setSubscription(entry.subscription);

    return () => {
      releaseSubscription(channel, params, callbacks);
      setSubscription(null);
      setIsConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumer, identifier, enabled]);

  return {
    subscription,
    isConnected,
  };
};
